import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import { translate } from '@/i18n'
import { ApiError } from '@/types/api'

export type NotificationType = 'error' | 'success' | 'info'

export interface AppNotification {
  id: number
  type: NotificationType
  message: string
}

/** Product requirement: notifications disappear on their own after 5 seconds. */
const DEFAULT_DURATION_MS = 5_000
const SIDEBAR_STORAGE_KEY = 'travick.sidebarCollapsed'

function readSidebarPreference(): boolean {
  try {
    return window.localStorage.getItem(SIDEBAR_STORAGE_KEY) === 'true'
  } catch {
    return false
  }
}

/** Transient UI state: toast notifications and sidebar layout. */
export const useUiStore = defineStore('ui', () => {
  const notifications = ref<AppNotification[]>([])
  /** Desktop: the sidebar is narrowed to icons only. */
  const sidebarCollapsed = ref(readSidebarPreference())
  /** Mobile: the sidebar is an off-canvas drawer, so it needs its own state. */
  const mobileSidebarOpen = ref(false)
  /**
   * Operazioni bloccanti in corso.
   *
   * È un contatore e non un booleano perché due operazioni possono sovrapporsi:
   * con un flag, la prima a finire spegnerebbe l'overlay mentre la seconda sta
   * ancora lavorando.
   */
  const pendingTasks = ref(0)

  const isBusy = computed(() => pendingTasks.value > 0)

  const timers = new Map<number, ReturnType<typeof setTimeout>>()
  let nextId = 1

  function dismiss(id: number): void {
    const timer = timers.get(id)
    if (timer !== undefined) {
      clearTimeout(timer)
      timers.delete(id)
    }
    notifications.value = notifications.value.filter((item) => item.id !== id)
  }

  function push(type: NotificationType, message: string, duration = DEFAULT_DURATION_MS): number {
    const id = nextId++
    notifications.value = [...notifications.value, { id, type, message }]

    if (duration > 0) {
      timers.set(
        id,
        setTimeout(() => dismiss(id), duration),
      )
    }
    return id
  }

  function notifyError(message: string): number {
    return push('error', message)
  }

  function notifySuccess(message: string): number {
    return push('success', message)
  }

  function notifyInfo(message: string): number {
    return push('info', message)
  }

  /**
   * Renders an ApiError as a red toast. Errors we generate client-side carry an
   * i18n key; backend messages are already human-readable English strings.
   */
  function notifyApiError(error: unknown): void {
    if (error instanceof ApiError) {
      notifyError(error.translationKey ? translate(error.translationKey) : error.message)
      return
    }
    notifyError(translate('errors.unexpected'))
  }

  function clearNotifications(): void {
    for (const timer of timers.values()) clearTimeout(timer)
    timers.clear()
    notifications.value = []
  }

  function setSidebarCollapsed(collapsed: boolean): void {
    sidebarCollapsed.value = collapsed
    try {
      window.localStorage.setItem(SIDEBAR_STORAGE_KEY, String(collapsed))
    } catch {
      // Non-fatal: the preference just won't be remembered.
    }
  }

  function toggleSidebar(): void {
    setSidebarCollapsed(!sidebarCollapsed.value)
  }

  /**
   * Segnala all'utente che sta succedendo qualcosa che vale l'attesa.
   *
   * Nato per il cambio lingua, che ricarica tutte le stringhe dell'interfaccia,
   * ma volutamente generico: qualunque operazione bloccante può usarlo.
   *
   * ⚠️ Usare `withLoading` quando possibile: il decremento **deve** avvenire in
   * `finally`, altrimenti una chiamata che fallisce lascia l'overlay appeso e
   * l'app inutilizzabile.
   */
  function beginLoading(): void {
    pendingTasks.value++
  }

  function endLoading(): void {
    pendingTasks.value = Math.max(0, pendingTasks.value - 1)
  }

  async function withLoading<T>(task: () => Promise<T>): Promise<T> {
    beginLoading()
    try {
      return await task()
    } finally {
      endLoading()
    }
  }

  function toggleMobileSidebar(): void {
    mobileSidebarOpen.value = !mobileSidebarOpen.value
  }

  function closeMobileSidebar(): void {
    mobileSidebarOpen.value = false
  }

  return {
    notifications,
    sidebarCollapsed,
    mobileSidebarOpen,
    isBusy,
    beginLoading,
    endLoading,
    withLoading,
    toggleMobileSidebar,
    closeMobileSidebar,
    push,
    dismiss,
    notifyError,
    notifySuccess,
    notifyInfo,
    notifyApiError,
    clearNotifications,
    setSidebarCollapsed,
    toggleSidebar,
  }
})
